import { useCallback, useState } from "react";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useCachedDataLoader } from "../../../../common/hooks/use_cached_data_loader";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useObrasListaFilter } from "../hooks/Obras_lista/filter";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useObrasListaPagination } from "../hooks/Obras_lista/pagination";
import { Obra } from "../../domain/models/obra";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { CopyObraForm } from "../schemas/Obras_lista/copy_obra";
import { EmailForm } from "../../../../common/presentation/schemas/email";
import { emailTemplates } from "../../../../common/utils/email_templates";
import { useReportViewer } from "../../../../config/hooks/utils/report_viewer";
import { TelasIds } from "../../../../common/utils/tela_ids";
import { MODO_CADASTRO_EMPREENDIMENTO } from "./obras_informacoes_gerais_view_model";

import CommonConstants from "../../../../common/constants";

/**
 * LoadObrasFuncState
 */

type LoadObrasFuncState = {
  tipoPesquisa?: number;
  valueField?: string | null;
  statusDocumentos?: number;
  resistenciaCompressao?: number;
  statusEquipamentos?: number;
  statusObras?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * ObrasListaViewModel
 */

export const useObrasListaViewModel = () => {
  const {
    commons: { getCentroCustoDropDownUseCase },
    reports: { getReportPlanoQualidadeObraUseCase },
    obras: { getEmpreendimentoDropDownUseCase, getAllObraUseCase, deleteObraUseCase, copyObraUseCase, sendEmailUseCase },
  } = useDependencyInjection();

  const backOrNavigate = useBackOrNavigate();

  const debouncedRef = useCreateDebounceHandler(750);

  const { pushInfoNotification, pushErrorNotification, setPageSaving } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [emailSendTo, setEmailSendTo] = useState("");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailAttachment, setEmailAttachment] = useState("");
  const [emailBody, setEmailBody] = useState("");

  const [obras, setObras] = useState<Obra[]>();

  const [selectedObra, setSelectedObra] = useState<Obra>();

  const [openIrParaModal, setOpenIrParaModal] = useState(false);
  const [openCopyModal, setOpenCopyModal] = useState(false);
  const [openTipoCadastroModal, setOpenTipoCadastroModal] = useState(false);
  const [tipoCadastroNewTab, setTipoCadastroNewTab] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [openEmailModal, setOpenEmailModal] = useState(false);

  const filters = useObrasListaFilter();
  const pagination = useObrasListaPagination();

  const [reportViewerState, startReportViewerProcess] = useReportViewer();

  /**
   * Funções de carregamento
   */

  const loadCentroCustoDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.centroCustoDropDown,
    loader: async () => await getCentroCustoDropDownUseCase.getCentroCustoDropDown(),
    onSet: (dt) => useObrasListaFilter.setState({ centroCustosDropdown: dt }),
    onError: (e) => {
      throw e;
    },
  });

  const loadEmpreendimentoDropdown = useCachedDataLoader({
    key: CommonConstants.cacheKey.empreendimentoDropDown,
    loader: async () => await getEmpreendimentoDropDownUseCase.getEmpreendimentoDropdown(),
    onSet: (dt) => useObrasListaFilter.setState({ empreendimentosDropdown: dt }),
    onError: (e) => {
      throw e;
    },
  });

  const loadObras = useCallback(
    async (funcState?: LoadObrasFuncState) => {
      try {
        const filteredTipoPesquisa = evaluateParamValue(funcState?.tipoPesquisa, filters.tipoPesquisa);
        const filteredValueField = evaluateParamValue(funcState?.valueField, filters.valueField);
        const filteredStatusDocumentos = evaluateParamValue(funcState?.statusDocumentos, filters.statusDocumentos);
        const filteredResistenciaCompressao = evaluateParamValue(funcState?.resistenciaCompressao, filters.resistenciaCompressao);
        const filteredStatusEquipamentos = evaluateParamValue(funcState?.statusEquipamentos, filters.statusEquipamentos);
        const filteredStatusObras = evaluateParamValue(funcState?.statusObras, filters.statusObras);

        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

        await getAllObraUseCase.getAllObra(
          filteredTipoPesquisa,
          filteredValueField,
          filteredStatusDocumentos,
          filteredResistenciaCompressao,
          filteredStatusEquipamentos,
          filteredStatusObras,
          filteredPage,
          filteredRowsPerPage,
        );

        setObras(getAllObraUseCase.items);

        pagination.setTotalItems(getAllObraUseCase.totalItems);
      } catch (e: unknown) {
        setObras(undefined);
        pagination.setTotalItems(0);
        throw e;
      }
    },
    [filters, pagination, getAllObraUseCase],
  );

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadCentroCustoDropdown();
      await loadEmpreendimentoDropdown();
      await loadObras();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar dados da pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadCentroCustoDropdown();
      await loadEmpreendimentoDropdown();
      await loadObras();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [loadCentroCustoDropdown, loadEmpreendimentoDropdown, loadObras, pushErrorNotification]);

  const handleCadastroObraOpenState = useCallback(
    (idObra: number | null, openNewTab: boolean) => {
      if (idObra) {
        if (openNewTab) {
          window.open(`/Obras_informacoesgerais?id=${idObra}`, "_blank", "noopener,noreferrer");
        } else {
          backOrNavigate(`/Obras_informacoesgerais?id=${idObra}`);
        }
      } else {
        if (openNewTab) {
          window.open(`/Obras_informacoesgerais`, "_blank", "noopener,noreferrer");
        } else {
          backOrNavigate("/Obras_informacoesgerais");
        }
      }
    },
    [backOrNavigate],
  );

  const handleTipoCadastroModalOpenState = useCallback(
    (openState: boolean, openNewTab: boolean = false) => {
      setTipoCadastroNewTab(openNewTab);
      setOpenTipoCadastroModal(openState);
    },
    [],
  );

  const handleTipoCadastroSelect = useCallback(
    (porEmpreendimento: boolean, openNewTab: boolean = false) => {
      const url = porEmpreendimento
        ? `/Obras_informacoesgerais?modo=${MODO_CADASTRO_EMPREENDIMENTO}`
        : "/Obras_informacoesgerais";

      setOpenTipoCadastroModal(false);

      if (tipoCadastroNewTab || openNewTab) {
        window.open(url, "_blank", "noopener,noreferrer");
      } else {
        backOrNavigate(url);
      }
    },
    [tipoCadastroNewTab, backOrNavigate],
  );

  const handleDeleteModalOpenState = useCallback(
    (idObra: number | null, openState: boolean) => {
      if (!loading && !saving) {
        if (openState) {
          if (idObra && obras) {
            setSelectedObra(obras.find((x) => x.idObra === idObra));
          } else {
            setSelectedObra(undefined);
          }
        }
        setOpenDeleteModal(openState);
      }
    },
    [loading, saving, obras],
  );

  const handleConfirmarDeletarObra = useCallback(async () => {
    try {
      setSaving(true);
      if (selectedObra) {
        await deleteObraUseCase.deleteObra(TelasIds.Obras_lista, selectedObra.idObra);
        await loadObras();
        pushInfoNotification("Sucesso", "Registro excluido com sucesso!");
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao excluir item!"));
    } finally {
      setSaving(false);
      setOpenDeleteModal(false);
    }
  }, [selectedObra, deleteObraUseCase, loadObras, pushErrorNotification, pushInfoNotification]);

  const handleCopyModalOpenState = useCallback(
    (idObra: number | null, openState: boolean) => {
      if (openState) {
        if (idObra && obras) {
          setSelectedObra(obras.find((x) => x.idObra === idObra));
        } else {
          setSelectedObra(undefined);
        }
      }
      setOpenCopyModal(openState);
    },
    [obras],
  );

  const handleConfirmCopy = useCallback(
    async (form: CopyObraForm) => {
      try {
        setLoading(true);
        setSaving(true);
        setPageSaving(true);

        if (selectedObra) {
          await copyObraUseCase.copyObra(selectedObra.idObra, form.idCentroCusto, form.copiarChecklist);
          await loadObras();
          setOpenCopyModal(false);
          pushInfoNotification("Sucesso", "Registro copiado com sucesso!");
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao copiar o item!"));
      } finally {
        setLoading(false);
        setSaving(false);
        setPageSaving(false);
      }
    },
    [selectedObra, copyObraUseCase, loadObras, setPageSaving, pushInfoNotification, pushErrorNotification],
  );

  const handleTipoPesquisaChange = useDebouncedCallback(
    debouncedRef,
    (tipoPesquisa: number) => {
      useObrasListaFilter.setState((prevState) => {
        if (prevState.tipoPesquisa === 1 && tipoPesquisa !== 1) {
          return { ...prevState, tipoPesquisa, valueField: null };
        }
        if (prevState.tipoPesquisa === 2 && tipoPesquisa !== 2) {
          return { ...prevState, tipoPesquisa, valueField: null };
        }
        if (prevState.tipoPesquisa !== 1 && tipoPesquisa === 1) {
          return { ...prevState, tipoPesquisa, valueField: null };
        }
        if (prevState.tipoPesquisa !== 2 && tipoPesquisa === 2) {
          return { ...prevState, tipoPesquisa, valueField: null };
        }
        return { ...prevState, tipoPesquisa };
      });
      return async () => {
        try {
          setLoading(true);
          useObrasListaPagination.setState({ page: 1 });
          await loadObras({ tipoPesquisa, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [pushErrorNotification, loadObras],
  );

  const handleValueFieldChange = useDebouncedCallback(
    debouncedRef,
    (valueField: string | null) => {
      useObrasListaFilter.setState({ valueField });
      return async () => {
        try {
          setLoading(true);
          useObrasListaPagination.setState({ page: 1 });
          await loadObras({ valueField, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [pushErrorNotification, loadObras],
  );

  const handleStatusDocumentosChange = useDebouncedCallback(
    debouncedRef,
    (statusDocumentos: number) => {
      useObrasListaFilter.setState({ statusDocumentos });
      return async () => {
        try {
          setLoading(true);
          useObrasListaPagination.setState({ page: 1 });
          await loadObras({ statusDocumentos, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [pushErrorNotification, loadObras],
  );

  const handleResistenciaCompressaoChange = useDebouncedCallback(
    debouncedRef,
    (resistenciaCompressao: number) => {
      useObrasListaFilter.setState({ resistenciaCompressao });
      return async () => {
        try {
          setLoading(true);
          useObrasListaPagination.setState({ page: 1 });
          await loadObras({ resistenciaCompressao, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [pushErrorNotification, loadObras],
  );

  const handleStatusEquipamentosCompressaoChange = useDebouncedCallback(
    debouncedRef,
    (statusEquipamentos: number) => {
      useObrasListaFilter.setState({ statusEquipamentos });
      return async () => {
        try {
          setLoading(true);
          useObrasListaPagination.setState({ page: 1 });
          await loadObras({ statusEquipamentos, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [pushErrorNotification, loadObras],
  );

  const handleStatusObrasChange = useCallback(async (statusObras: string) => {
    try {
      setLoading(true);
      useObrasListaFilter.setState({ statusObras: Number(statusObras) });
      useObrasListaPagination.setState({ page: 1 });
      await loadObras({ statusObras: Number(statusObras), page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadObras,
    pushErrorNotification,
  ]);

  const handleInformacaoClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      useObrasListaFilter.getState().clearFilters();
      await loadObras({
        tipoPesquisa: 1,
        valueField: null,
        statusDocumentos: 0,
        resistenciaCompressao: 0,
        statusEquipamentos: 0,
      });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
    } finally {
      setLoading(false);
    }
  }, [pushErrorNotification, loadObras]);

  const handlePageChange = useCallback(
    async (page: number) => {
      try {
        setLoading(true);
        useObrasListaPagination.setState({ page: page + 1 });
        await loadObras({ page: page + 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, loadObras],
  );

  const handleRowsPerPageChange = useCallback(
    async (rowsPerPage: number) => {
      try {
        setLoading(true);
        useObrasListaPagination.setState({ page: 1, rowsPerPage });
        await loadObras({ page: 1, rowsPerPage });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
      } finally {
        setLoading(false);
      }
    },
    [pushErrorNotification, loadObras],
  );

  const handleReportPlanoQualidadeSubmit = useCallback(
    async (idObra: number | null, _: boolean) => {
      if (idObra) {
        try {
          setLoading(true);

          await startReportViewerProcess({
            fetchCallback: async (requiredMimeType) =>
              await getReportPlanoQualidadeObraUseCase.getReportPlanoQualidadeObra(requiredMimeType, idObra),
          });
        } catch (e: unknown) {
          pushErrorNotification("Erro", processError(e, "Falha ao buscar dados da pagina!"));
        } finally {
          setLoading(false);
        }
      }
    },
    [getReportPlanoQualidadeObraUseCase, startReportViewerProcess, pushErrorNotification],
  );

  const handleOpenEmailOpenState = useCallback((idObra: number | null, openState: boolean) => {
    if (openState) {
      if (idObra && obras) {
        const obra = obras.find((x) => x.idObra === idObra);
        if (obra) {
          const centroCusto = obra.centroCusto ?? "";
          setEmailSendTo("");
          setEmailSubject("Plano de Qualidade da Obra: " + centroCusto);
          setEmailAttachment(`Obra_${centroCusto.replaceAll("-", "_")}.pdf`);
          setEmailBody(emailTemplates.obra.replaceAll("{CENTRO_CUSTO}", centroCusto));
          setSelectedObra(obra);
          setOpenEmailModal(openState);
        }
      }
    } else {
      setOpenEmailModal(openState);
    }
  },
    [
      obras,
    ]);

  const handleEmailSubmit = useCallback(async (form: EmailForm) => {
    if (selectedObra) {
      try {
        setLoading(true);
        await sendEmailUseCase.sendEmail(
          selectedObra.idObra,
          form.sendTo,
          form.subject,
          form.body,
          emailAttachment,
        );

        pushInfoNotification("Sucesso", "Email enviado com sucesso!");
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao enviar email!"));
      } finally {
        setLoading(false);
        setSelectedObra(undefined);
        setOpenEmailModal(false);
      }
    }
  }, [
    selectedObra,
    emailAttachment,
    sendEmailUseCase,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleIrParaModalOpenState = useCallback(
    (idObra: number | null, openState: boolean) => {
      if (openState) {
        if (idObra && obras) {
          setSelectedObra(obras.find((x) => x.idObra === idObra));
        } else {
          setSelectedObra(undefined);
        }
      }
      setOpenIrParaModal(openState);
    },
    [obras],
  );

  const handleIrParaExecuteAction = useCallback(
    (idAction: number, idObra: number | null, openNewTab: boolean) => {
      if (idObra) {
        switch (idAction) {
          case CommonConstants.obrasActions.informacoesGerais:
            if (openNewTab) {
              window.open(`/Obras_informacoesgerais?id=${idObra}`, "_blank", "noopener,noreferrer");
            } else {
              backOrNavigate(`/Obras_informacoesgerais?id=${idObra}`);
            }
            break;

          case CommonConstants.obrasActions.gestaoServicos:
            if (openNewTab) {
              window.open(`/Obras_gestao_servicos?id=${idObra}`, "_blank", "noopener,noreferrer");
            } else {
              backOrNavigate(`/Obras_gestao_servicos?id=${idObra}`);
            }
            break;

          case CommonConstants.obrasActions.checklistArea:
            if (openNewTab) {
              window.open(`/Obras_cadastroarea?id=${idObra}`, "_blank", "noopener,noreferrer");
            } else {
              backOrNavigate(`/Obras_cadastroarea?id=${idObra}`);
            }
            break;

          case CommonConstants.obrasActions.mapaConcretagem:
            if (openNewTab) {
              window.open(`/Obras_concretagem?id=${idObra}`, "_blank", "noopener,noreferrer");
            } else {
              backOrNavigate(`/Obras_concretagem?id=${idObra}`);
            }
            break;

          case CommonConstants.obrasActions.equipamentoMedicao:
            if (openNewTab) {
              window.open(`/Obras_equipamentosmedicao?id=${idObra}`, "_blank", "noopener,noreferrer");
            } else {
              backOrNavigate(`/Obras_equipamentosmedicao?id=${idObra}`);
            }
            break;

          case CommonConstants.obrasActions.sustentabilidade:
            if (openNewTab) {
              window.open(`/Obras_sustentabilidade?id=${idObra}`, "_blank", "noopener,noreferrer");
            } else {
              backOrNavigate(`/Obras_sustentabilidade?id=${idObra}`);
            }
            break;

          case CommonConstants.obrasActions.equipamentoCritico:
            if (openNewTab) {
              window.open(`/Obras_maquinasequipamentos?id=${idObra}`, "_blank", "noopener,noreferrer");
            } else {
              backOrNavigate(`/Obras_maquinasequipamentos?id=${idObra}`);
            }
            break;

          case CommonConstants.obrasActions.treinamentos:
            if (openNewTab) {
              window.open(`/Obras_treinamentos?id=${idObra}`, "_blank", "noopener,noreferrer");
            } else {
              backOrNavigate(`/Obras_treinamentos?id=${idObra}`);
            }
            break;

          case CommonConstants.obrasActions.gestaoResiduos:
            if (openNewTab) {
              window.open(`/Obras_gestaoresiduos?id=${idObra}`, "_blank", "noopener,noreferrer");
            } else {
              backOrNavigate(`/Obras_gestaoresiduos?id=${idObra}`);
            }
            break;

          case CommonConstants.obrasActions.alvenariaEstrutural:
            if (openNewTab) {
              window.open(`/Obras_alvenariaestrutural?id=${idObra}`, "_blank", "noopener,noreferrer");
            } else {
              backOrNavigate(`/Obras_alvenariaestrutural?id=${idObra}`);
            }
            break;

          case CommonConstants.obrasActions.relacaoMateriaisControlados:
            if (openNewTab) {
              window.open(`/Obras_materiaiscontrolados?id=${idObra}`, "_blank", "noopener,noreferrer");
            } else {
              backOrNavigate(`/Obras_materiaiscontrolados?id=${idObra}`);
            }
            break;

          case CommonConstants.obrasActions.relacaoServicosControlados:
            if (openNewTab) {
              window.open(`/Obras_servicoscontrolados?id=${idObra}`, "_blank", "noopener,noreferrer");
            } else {
              backOrNavigate(`/Obras_servicoscontrolados?id=${idObra}`);
            }
            break;

          case CommonConstants.obrasActions.documentos:
            if (openNewTab) {
              window.open(`/Obras_documentos?id=${idObra}`, "_blank", "noopener,noreferrer");
            } else {
              backOrNavigate(`/Obras_documentos?id=${idObra}`, { replace: true, alternativeRoute: "/Obras_lista" });
            }
            break;

        }
      }
    },
    [backOrNavigate],
  );

  return {
    loading,
    saving,
    errorMessage,
    emailSendTo,
    emailSubject,
    emailAttachment,
    emailBody,
    obras: obras ?? [],
    selectedObra,
    openIrParaModal,
    openCopyModal,
    openTipoCadastroModal,
    openDeleteModal,
    openEmailModal,

    filters,
    pagination,
    reportViewerState,

    dropdown: {
      centroCustosDropdown: filters.centroCustosDropdown ?? [],
      empreendimentosDropdown: filters.empreendimentosDropdown ?? [],
    },

    handleReload,
    handleCadastroObraOpenState,
    handleTipoCadastroModalOpenState,
    handleTipoCadastroSelect,
    handleDeleteModalOpenState,
    handleConfirmarDeletarObra,
    handleCopyModalOpenState,
    handleConfirmCopy,
    handleTipoPesquisaChange,
    handleValueFieldChange,
    handleStatusDocumentosChange,
    handleResistenciaCompressaoChange,
    handleStatusEquipamentosCompressaoChange,
    handleStatusObrasChange,
    handleInformacaoClearFilters,
    handlePageChange,
    handleRowsPerPageChange,
    handleReportPlanoQualidadeSubmit,
    handleOpenEmailOpenState,
    handleEmailSubmit,
    handleIrParaModalOpenState,
    handleIrParaExecuteAction,
  };
};
